/**
 * QC Tasks — role === 'qc' Tasks tab.
 *
 * Replaces the old generic to-do workflow for QC users.
 * Backend: GET  /api/qc/insights  (same dataset the QC Insights cards use)
 *          POST /api/qc/lead/{lead_id}/ota/{ota_index}/start | complete
 *
 * Layout:
 *   Tab 1 — Active QC   (non-completed QC tasks)
 *             • Today Assigned QC  — assigned_today === true
 *             • All QC             — every non-completed task
 *             (Today / All is a toggle so a task is never double-counted)
 *   Tab 2 — Completed QC (completed tasks, respects date filter)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ShieldCheck, CheckCircle2, RefreshCw, Loader2, ExternalLink, Play, Check, Clock, CalendarClock, ListChecks,
} from 'lucide-react';
import { toast } from 'sonner';
import DateRangeFilter, { rangeParams } from '../../components/DateRangeFilter';
import { useTaskTable, TaskSearchBar, TablePagination } from '../../components/tasks/TableControls';

const SEARCH_KEYS = ['hotel_name', 'ota_name'];

const OVERDUE_PALETTE = {
  Late:     'bg-amber-100 text-amber-700 border-amber-200',
  Urgent:   'bg-orange-100 text-orange-700 border-orange-200',
  Critical: 'bg-rose-100 text-rose-700 border-rose-200',
};
const STATUS_BADGE = {
  'Not Started': 'bg-gray-100 text-gray-600 border-gray-200',
  'In Progress': 'bg-blue-100 text-blue-700 border-blue-200',
  'Complete':    'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const QCTable = ({ rows, busyKey, onAct, onComplete, tableTestId }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid={tableTestId}>
      <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
        <tr>
          <th className="px-4 py-2.5 text-left">Hotel</th>
          <th className="px-4 py-2.5 text-left">OTA</th>
          <th className="px-4 py-2.5 text-left">Status</th>
          <th className="px-4 py-2.5 text-left">Start</th>
          <th className="px-4 py-2.5 text-left">Complete</th>
          <th className="px-4 py-2.5 text-left">Deadline</th>
          <th className="px-4 py-2.5 text-left">Overdue</th>
          <th className="px-4 py-2.5 text-left">Overdue Time</th>
          <th className="px-4 py-2.5 text-right">Action</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.length === 0 && (
          <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400 text-xs">No QC tasks here.</td></tr>
        )}
        {rows.map((r) => {
          const ov = (r.overdue_label || '').trim();
          const ovCls = OVERDUE_PALETTE[ov] || null;
          const statusCls = STATUS_BADGE[r.status] || 'bg-gray-100 text-gray-600 border-gray-200';
          const isComplete = r.status === 'Complete';
          const isInProgress = r.status === 'In Progress';
          const startBusy = busyKey === `${r.lead_id}:${r.ota_index}:start`;
          const completeBusy = busyKey === `${r.lead_id}:${r.ota_index}:complete`;
          return (
            <tr key={`${r.lead_id}:${r.ota_index}`} className="hover:bg-gray-50" data-testid={`qc-task-row-${r.lead_id}-${r.ota_index}`}>
              <td className="px-4 py-2.5 font-medium text-gray-800">{r.hotel_name || '—'}</td>
              <td className="px-4 py-2.5 text-gray-700">
                {r.ota_name || '—'}
                {r.listing_url && (
                  <a href={r.listing_url} target="_blank" rel="noopener noreferrer" className="ml-2 inline-flex items-center text-teal-600 hover:underline text-xs"><ExternalLink size={11} /></a>
                )}
              </td>
              <td className="px-4 py-2.5">
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${statusCls}`} data-testid={`qc-task-status-${r.lead_id}-${r.ota_index}`}>{r.status}</span>
              </td>
              <td className="px-4 py-2.5 text-xs text-gray-600 whitespace-nowrap">{fmtDate(r.start_date)}</td>
              <td className="px-4 py-2.5 text-xs text-gray-600 whitespace-nowrap">{fmtDate(r.end_date)}</td>
              <td className="px-4 py-2.5 text-xs text-gray-600 whitespace-nowrap">{fmtDate(r.deadline)}</td>
              <td className="px-4 py-2.5">
                {ov ? <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${ovCls}`}>{ov}</span> : <span className="text-gray-300 text-xs">—</span>}
              </td>
              <td className="px-4 py-2.5 text-xs text-gray-600 whitespace-nowrap">{r.overdue_time || <span className="text-gray-300">—</span>}</td>
              <td className="px-4 py-2.5 text-right">
                {!isComplete && !isInProgress && (
                  <button onClick={() => onAct(r.lead_id, r.ota_index, 'start')} disabled={startBusy}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    data-testid={`qc-task-start-${r.lead_id}-${r.ota_index}`}>
                    {startBusy ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />} Start
                  </button>
                )}
                {isInProgress && (
                  <button onClick={() => onComplete(r)} disabled={completeBusy}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50"
                    data-testid={`qc-task-complete-${r.lead_id}-${r.ota_index}`}>
                    {completeBusy ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />} Complete
                  </button>
                )}
                {isComplete && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-emerald-50 text-emerald-700 rounded-md" data-testid={`qc-task-done-${r.lead_id}-${r.ota_index}`}><Clock size={11} /> Done</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const QCTasks = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  // Tasks date filter has NO default (user picks).
  const [range, setRange] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [tab, setTab] = useState('active');     // active | completed
  const [activeScope, setActiveScope] = useState('all'); // today | all
  // Phase-1 restructure — QC now captures Hotel ID + Listing URL on completion.
  const [completeRow, setCompleteRow] = useState(null);
  const [qcHotelId, setQcHotelId] = useState('');
  const [qcListingUrl, setQcListingUrl] = useState('');
  const [qcErr, setQcErr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const authHeader = () => {
    const t = localStorage.getItem('token') || localStorage.getItem('access_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams(rangeParams(range, customFrom, customTo)).toString();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/qc/insights?${qs}`, { headers: authHeader() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) { setError(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [range, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);

  const act = async (leadId, otaIndex, action) => {
    const key = `${leadId}:${otaIndex}:${action}`;
    setBusyKey(key);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/qc/lead/${leadId}/ota/${otaIndex}/${action}`,
        { method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' } },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      toast.success(action === 'start' ? 'QC started' : 'QC marked complete');
      await load();
    } catch (e) { toast.error(e.message || `${action} failed`); }
    finally { setBusyKey(null); }
  };

  const rows = useMemo(() => data?.rows || [], [data]);

  const openComplete = (r) => {
    setCompleteRow(r);
    setQcHotelId(r.hotel_id || '');
    setQcListingUrl(r.listing_url || '');
    setQcErr('');
  };

  const submitQcComplete = async () => {
    if (!completeRow) return;
    const hid = qcHotelId.trim();
    const url = qcListingUrl.trim();
    if (!hid) { setQcErr('Hotel ID is required'); return; }
    let valid = false;
    try { new URL(url); valid = true; } catch { valid = false; }
    if (!url || !valid) { setQcErr('A valid Listing URL is required'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/qc/lead/${completeRow.lead_id}/ota/${completeRow.ota_index}/complete`,
        { method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ hotel_id: hid, listing_url: url }) },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      toast.success('QC completed — Hotel ID & Listing URL saved');
      setCompleteRow(null);
      await load();
    } catch (e) { setQcErr(e.message || 'Complete failed'); }
    finally { setSubmitting(false); }
  };

  const activeRows = useMemo(() => rows.filter((r) => r.status !== 'Complete'), [rows]);
  const completedRows = useMemo(() => rows.filter((r) => r.status === 'Complete'), [rows]);
  const todayRows = useMemo(() => activeRows.filter((r) => r.assigned_today), [activeRows]);
  const shownActive = activeScope === 'today' ? todayRows : activeRows;
  // Independent property search + pagination over the currently shown table.
  const shown = tab === 'completed' ? completedRows : shownActive;
  const t = useTaskTable(shown, { keys: SEARCH_KEYS, pageSize: 10, resetKey: `${tab}:${activeScope}` });

  return (
    <div className="space-y-5" data-testid="qc-tasks">
      <div className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2"><ShieldCheck size={18} className="text-fuchsia-600" /> QC Tasks</h2>
          <p className="text-sm text-gray-500 mt-0.5">Start the timer and complete the Quality Checks assigned to you.</p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          data-testid="qc-tasks-refresh">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3" data-testid="qc-tasks-error">{error}</div>}

      <DateRangeFilter value={range} onChange={setRange} customFrom={customFrom} customTo={customTo}
        onCustomFrom={setCustomFrom} onCustomTo={setCustomTo} testId="qc-tasks-date-filter" />

      {/* Tab 1 / Tab 2 */}
      <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 shadow-sm" data-testid="qc-tasks-tabs">
        <button onClick={() => setTab('active')} data-testid="qc-tasks-tab-active"
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === 'active' ? 'bg-fuchsia-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
          Active QC <span className="ml-1 opacity-80">({activeRows.length})</span>
        </button>
        <button onClick={() => setTab('completed')} data-testid="qc-tasks-tab-completed"
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === 'completed' ? 'bg-emerald-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
          Completed QC <span className="ml-1 opacity-80">({completedRows.length})</span>
        </button>
      </div>

      <TaskSearchBar query={t.query} setQuery={t.setQuery} testId="qc-tasks-search" />

      {loading && !data ? (
        <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 size={24} className="animate-spin" /></div>
      ) : tab === 'completed' ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="qc-tasks-completed-wrap">
          <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2">
            <CheckCircle2 size={16} className="text-emerald-600" />
            <h3 className="text-sm font-bold text-gray-800">Completed QC</h3>
            <span className="ml-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-[11px] font-medium" data-testid="qc-tasks-completed-count">{completedRows.length}</span>
          </div>
          <QCTable rows={t.pageRows} busyKey={busyKey} onAct={act} onComplete={openComplete} tableTestId="qc-tasks-completed-table" />
          <TablePagination page={t.page} totalPages={t.totalPages} setPage={t.setPage} total={t.total} pageSize={t.pageSize} testId="qc-tasks-completed-pagination" />
        </div>
      ) : (
        <>
          {/* Today / All toggle (a task appears once — never double-counted) */}
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 shadow-sm" data-testid="qc-tasks-scope-toggle">
            <button onClick={() => setActiveScope('today')} data-testid="qc-tasks-scope-today"
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1 ${activeScope === 'today' ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              <CalendarClock size={14} /> Today Assigned QC <span className="ml-1 opacity-80">({todayRows.length})</span>
            </button>
            <button onClick={() => setActiveScope('all')} data-testid="qc-tasks-scope-all"
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1 ${activeScope === 'all' ? 'bg-slate-700 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              <ListChecks size={14} /> All QC <span className="ml-1 opacity-80">({activeRows.length})</span>
            </button>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="qc-tasks-active-wrap">
            <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2">
              <ShieldCheck size={16} className="text-fuchsia-600" />
              <h3 className="text-sm font-bold text-gray-800">{activeScope === 'today' ? 'Today Assigned QC' : 'All QC'}</h3>
              <span className="ml-1 px-2 py-0.5 bg-fuchsia-50 text-fuchsia-700 rounded-full text-[11px] font-medium" data-testid="qc-tasks-active-count">{shownActive.length}</span>
            </div>
            <QCTable rows={t.pageRows} busyKey={busyKey} onAct={act} onComplete={openComplete} tableTestId="qc-tasks-active-table" />
            <TablePagination page={t.page} totalPages={t.totalPages} setPage={t.setPage} total={t.total} pageSize={t.pageSize} testId="qc-tasks-active-pagination" />
          </div>
        </>
      )}

      {/* QC Complete popup — mandatory Hotel ID + Listing URL (moved from CX) */}
      {completeRow && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={() => !submitting && setCompleteRow(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="qc-complete-modal">
            <h3 className="text-base font-bold text-gray-800 mb-1 flex items-center gap-2"><ShieldCheck size={18} className="text-emerald-600" /> Complete QC</h3>
            <p className="text-xs text-gray-500 mb-3">{completeRow.hotel_name} · {completeRow.ota_name}</p>
            <label className="block text-xs font-medium text-gray-600 mb-1">Hotel ID *</label>
            <input value={qcHotelId} onChange={(e) => setQcHotelId(e.target.value)} placeholder="Hotel ID" data-testid="qc-complete-hotel-id"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg mb-3 focus:ring-2 focus:ring-emerald-500 outline-none" />
            <label className="block text-xs font-medium text-gray-600 mb-1">Listing URL *</label>
            <input value={qcListingUrl} onChange={(e) => setQcListingUrl(e.target.value)} placeholder="https://..." data-testid="qc-complete-listing-url"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg mb-3 focus:ring-2 focus:ring-emerald-500 outline-none" />
            {qcErr && <p className="text-red-500 text-xs mb-2" data-testid="qc-complete-error">{qcErr}</p>}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setCompleteRow(null)} disabled={submitting} className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={submitQcComplete} disabled={submitting} data-testid="qc-complete-submit"
                className="px-4 py-1.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1">
                {submitting ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Submit & Complete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QCTasks;
